import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import locale files
import en from './locales/en.json';
import uk from './locales/uk.json';
import de from './locales/de.json';

// Supported languages configuration
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', nativeName: 'English', rtl: false },
  uk: { name: 'Ukrainian', nativeName: 'Українська', rtl: false },
  de: { name: 'German', nativeName: 'Deutsch', rtl: false },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Resources object with all translations
const resources = {
  en: { translation: en },
  uk: { translation: uk },
  de: { translation: de },
};

/**
 * Initialize i18n with the specified language
 * @param language - Initial language code
 * @param persistLanguage - Callback to persist language preference
 */
export function initI18n(
  language: SupportedLanguage = DEFAULT_LANGUAGE,
  persistLanguage?: (lang: SupportedLanguage) => void
) {
  i18n
    .use(initReactI18next)
    .init({
      resources,
      lng: language,
      fallbackLng: DEFAULT_LANGUAGE,
      compatibilityJSON: 'v3', // Use v3 format to avoid Intl.PluralRules requirement in React Native
      interpolation: {
        escapeValue: false, // React already escapes values
      },
      react: {
        useSuspense: false,
      },
    });

  // Listen for language changes to persist preference
  if (persistLanguage) {
    i18n.on('languageChanged', (lng) => {
      persistLanguage(lng as SupportedLanguage);
    });
  }

  return i18n;
}

/**
 * Change the current language
 */
export async function changeLanguage(language: SupportedLanguage): Promise<void> {
  await i18n.changeLanguage(language);
}

/**
 * Get the current language
 */
export function getCurrentLanguage(): SupportedLanguage {
  return (i18n.language || DEFAULT_LANGUAGE) as SupportedLanguage;
}

/**
 * Check if a language is RTL
 */
export function isRTL(language?: SupportedLanguage): boolean {
  const lang = language || getCurrentLanguage();
  return SUPPORTED_LANGUAGES[lang]?.rtl || false;
}

/**
 * Get list of available languages
 */
export function getAvailableLanguages(): Array<{
  code: SupportedLanguage;
  name: string;
  nativeName: string;
}> {
  return Object.entries(SUPPORTED_LANGUAGES).map(([code, config]) => ({
    code: code as SupportedLanguage,
    name: config.name,
    nativeName: config.nativeName,
  }));
}

export { i18n };
